import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardList, ArrowRight, BookOpen, GraduationCap, AlertTriangle, AlertCircle } from "lucide-react";
import { format, parseISO, isPast, isToday, differenceInDays } from "date-fns";
import { useUserAssignments, UserAssignment } from "@/hooks/useUserAssignments";

interface DueDateStatus {
  text: string;
  status: 'overdue' | 'urgent' | 'warning' | 'normal';
  daysUntilDue: number;
  displayText: string;
}

function getDueDateStatus(dueDate: string | null): DueDateStatus | null {
  if (!dueDate) return null;
  
  const now = new Date();
  const date = parseISO(dueDate);
  const daysUntilDue = differenceInDays(date, now);
  const isOverdue = isPast(date) && !isToday(date);
  const isDueToday = isToday(date);
  
  let status: DueDateStatus['status'];
  let displayText: string;
  
  if (isOverdue) {
    const daysOverdue = Math.abs(daysUntilDue);
    status = 'overdue';
    displayText = 'Overdue';
  } else if (isDueToday) {
    status = 'urgent';
    displayText = 'Due today';
  } else if (daysUntilDue <= 3) {
    status = 'urgent';
    displayText = daysUntilDue === 1 ? 'Due in 1 day' : `Due in ${daysUntilDue} days`;
  } else if (daysUntilDue <= 7) {
    status = 'warning';
    displayText = `Due in ${daysUntilDue} days`;
  } else {
    status = 'normal';
    displayText = format(date, "MMM d, yyyy");
  }
  
  return {
    text: format(date, "MMM d, yyyy"),
    status,
    daysUntilDue,
    displayText,
  };
}

function getUrgencyScore(assignment: UserAssignment): number {
  const dueDateStatus = getDueDateStatus(assignment.due_date);
  let score = 0;
  
  if (dueDateStatus?.status === 'overdue') {
    score = 1000 + Math.abs(dueDateStatus.daysUntilDue);
  } else if (dueDateStatus) {
    score = 100 - Math.min(dueDateStatus.daysUntilDue, 100);
  }
  
  if (assignment.required) {
    score += 50;
  }
  
  return score;
}

export function AssignmentsWidget() {
  const navigate = useNavigate();
  const { assignments, isLoading, count } = useUserAssignments();

  // Sort by urgency and get top 3
  const topAssignments = useMemo(() => {
    return [...assignments]
      .sort((a, b) => getUrgencyScore(b) - getUrgencyScore(a))
      .slice(0, 3);
  }, [assignments]);

  // Count urgent items
  const urgentCount = useMemo(() => {
    return assignments.filter(a => {
      const status = getDueDateStatus(a.due_date);
      return status?.status === 'overdue' || status?.status === 'urgent';
    }).length;
  }, [assignments]);

  // Don't render if user has no assignments (after loading)
  if (!isLoading && count === 0) {
    return null;
  }

  // Show skeleton loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <div className="space-y-3">
            <Skeleton className="h-16 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
          <Skeleton className="h-4 w-32" />
        </CardContent>
      </Card>
    );
  }

  const handleStartLearning = (type: 'curriculum' | 'course', itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === 'curriculum') {
      navigate(`/curricula/${itemId}`);
    } else {
      navigate(`/courses/${itemId}`);
    }
  };

  return (
    <Card className={urgentCount > 0 ? "border-warning/50" : ""}>
      <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
          <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
          <span className="hidden sm:inline">My Learning Assignments</span>
          <span className="sm:hidden">Assignments</span>
          {urgentCount > 0 && (
            <Badge variant="outline" className="ml-auto border-destructive text-destructive bg-destructive/10 text-xs">
              <AlertCircle className="h-3 w-3 mr-1" />
              {urgentCount} urgent
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-6">
        {/* Count message */}
        <p className="text-xs sm:text-sm text-muted-foreground">
          You have <span className="font-semibold text-foreground">{count}</span> active assignment{count !== 1 ? 's' : ''}
        </p>

        {/* Assignment list */}
        <div className="space-y-2 sm:space-y-3">
          {topAssignments.map((assignment) => {
            const dueDateStatus = getDueDateStatus(assignment.due_date);
            
            // Determine border color based on urgency
            let borderClass = "border-l-primary";
            if (dueDateStatus?.status === 'overdue') {
              borderClass = "border-l-destructive";
            } else if (dueDateStatus?.status === 'urgent') {
              borderClass = "border-l-destructive";
            } else if (dueDateStatus?.status === 'warning') {
              borderClass = "border-l-warning";
            }
            
            return (
              <div 
                key={assignment.id}
                className={`border rounded-lg p-2.5 sm:p-3 border-l-4 ${borderClass} bg-card`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {assignment.type === 'curriculum' ? (
                        <GraduationCap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className="font-medium text-xs sm:text-sm line-clamp-1">
                        {assignment.item_title}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                      {assignment.required && (
                        <Badge variant="secondary" className="text-[10px] sm:text-xs px-1.5 py-0">
                          Required
                        </Badge>
                      )}
                      
                      {dueDateStatus && (
                        <Badge 
                          variant={dueDateStatus.status === 'overdue' ? "destructive" : "outline"}
                          className={`text-[10px] sm:text-xs px-1.5 py-0 ${
                            dueDateStatus.status === 'urgent' 
                              ? 'border-destructive text-destructive bg-destructive/10' 
                              : dueDateStatus.status === 'warning'
                                ? 'border-warning text-warning bg-warning/10'
                                : ''
                          }`}
                        >
                          {(dueDateStatus.status === 'overdue' || dueDateStatus.status === 'urgent') && (
                            <AlertTriangle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                          )}
                          {dueDateStatus.displayText}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    size="sm" 
                    variant={dueDateStatus?.status === 'overdue' ? "destructive" : "outline"}
                    className="w-full sm:w-auto flex-shrink-0 h-8 text-xs sm:text-sm"
                    onClick={(e) => handleStartLearning(assignment.type, assignment.item_id, e)}
                  >
                    Start
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* View all link */}
        <Button 
          variant="link" 
          className="p-0 h-auto text-primary text-xs sm:text-sm" 
          onClick={() => navigate("/curricula?tab=assignments")}
        >
          View all assignments →
        </Button>
      </CardContent>
    </Card>
  );
}
